import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'scatter3D',
  name_en: '3D Scatter',
  // name_zh: '3D 散点图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    z: amount => amount > 1,
    size: amount => amount > 1,
    color: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    z: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    size: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE,
      optional: true
    },
    color: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE,
      optional: true
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z: string;
    size?: string;
    color?: string;
  };
}

/** 暂不支持legend的点击功能 */
export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
  render(api, spec, $dom) {
    const { layout, token, data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, z, size, color }
    } = spec;
    const data = dataArray[dataIndex];

    // * -------------------------- data
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const sizeRangeMap = {
      [util.const.LAYOUT.SMALL]: [8, 28],
      [util.const.LAYOUT.SMALL_2_1]: [8, 46],
      [util.const.LAYOUT.SMALL_1_2]: [8, 46],
      [util.const.LAYOUT.LARGE]: [16, 92],
      [util.const.LAYOUT.LARGE_3_2]: [20, 120]
    };
    const range = sizeRangeMap[layout];
    const {
      xAxisData,
      yAxisData,
      sizeData = [],
      colorData = []
    } = util.dataEncoding.encoding(data.values.slice(), {
      xAxisData: {
        field: x,
        transforms: [{ type: 'deDuplication' }]
      },
      yAxisData: {
        field: y,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      sizeData: {
        field: size!,
        transforms: [{ type: 'toNumeric' }]
      },
      colorData: {
        field: color!,
        transforms: [{ type: 'toNumeric' }]
      }
    });
    const sizeMax = Math.max(...(sizeData as number[]));
    const colorMax = Math.max(...(colorData as number[]));
    const colorMin = Math.min(...(colorData as number[]));
    const seriesData = data.values.map(item => {
      const result = [item[x], item[y], +item[z]];
      if (typeof size !== 'undefined') {
        result.push(+item[size]);
      }
      if (typeof color !== 'undefined') {
        result.push(+item[color]);
      }

      return result;
    });
    const isYAxisNumber = typeof data.values[0][y] === 'number';

    // * -------------------------- chart
    function generateColorSlices(min: number, max: number, colorList: string[]) {
      const numColors = colorList.length;
      const colorSlices = [];
      const span = max - min;
      const sliceSize = span / numColors;

      for (let i = 0; i < numColors; i++) {
        const colorSlice = {
          gt: min + i * sliceSize,
          lte: min + (i + 1) * sliceSize,
          color: colorList[i]
        };
        colorSlices.push(colorSlice);
      }

      return colorSlices;
    }

    const presetOption = api.hook.buildStandardChartPresetOption(api);
    const legend = {
      data: [
        {
          name: z,
          color: presetOption?.color?.[0] || '#5c7bd9',
          type: util.const.SERIES_2_LEGEND_TYPE_MAP.scatter3D,
          selected: true,
          disableSelect: true
        }
      ]
    };
    const option = {
      grid3D: {
        viewControl: {
          distance: layout === util.const.LAYOUT.SMALL_1_2 ? 400 : 200
        }
      },
      tooltip: {
        // eslint-disable-next-line
        formatter: function (val) {
          const { data: dataTemp, color: colorTemp, seriesType, name } = val;
          const result = hook.formatDataNumberValue(dataTemp[2], {
            type: 'table',
            unit: dataMetaInfo[z]?.unit
          });
          const items = [
            {
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[
                seriesType as keyof (typeof util)['const']['SERIES_2_LEGEND_TYPE_MAP']
              ],
              color: colorTemp,
              name: '',
              value: ''
            },
            {
              name: x,
              value: dataTemp[0]
            },
            {
              name: y,
              value: dataTemp[1]
            },
            {
              name: z,
              value: `${result.value + result.unit}`
            }
          ];

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: name || '',
            items
          });
        }
      },
      visualMap: [
        typeof size !== 'undefined' && {
          show: false,
          calculable: true,
          dimension: 3,
          max: sizeMax / 2,
          inRange: {
            symbolSize: [range[0] / 3, range[1] / 3]
          }
        },
        typeof color !== 'undefined' && {
          type: 'piecewise',
          show: false,
          calculable: true,
          dimension: typeof size === 'undefined' ? 3 : 4,
          pieces: generateColorSlices(colorMin, colorMax, presetOption?.color)
        }
      ].filter(Boolean),
      xAxis: [],
      yAxis: [],
      xAxis3D: {
        show: true,
        name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
        type: 'category',
        data: xAxisData
      },
      yAxis3D: {
        show: true,
        name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
        type: isYAxisNumber ? 'value' : 'category',
        axisLabel: isYAxisNumber
          ? {
              formatter(value) {
                const result = hook.formatDataNumberValue(value);
                return result.value + result.unit;
              }
            }
          : {},
        data: yAxisData
      },
      zAxis3D: {
        show: true,
        type: 'value',
        name: z + (dataMetaInfo[z]?.unit ? `(${dataMetaInfo[z].unit})` : ''),
        axisLabel: {
          formatter(value) {
            const result = hook.formatDataNumberValue(value);
            return result.value + result.unit;
          }
        }
      },
      series: [
        {
          type: 'scatter3D',
          data: seriesData,
          symbolSize: range[0],
          label: {
            formatter(dataTemp) {
              const value = dataTemp.data[2];
              const result = hook.formatDataNumberValue(value, {
                type: 'table',
                unit: dataMetaInfo[z]?.unit
              });
              return result.value + result.unit;
            }
          }
        }
      ]
    };

    return renderApi => {
      const renderParams = {
        option: renderApi.mergeChartOptionProxy(option)
      };

      renderApi.renderLegendProxy(legend.data);
      renderApi.renderChartProxy(renderParams, { useECharts: true });
    };
  }
});
